import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CONSENT_TEMPLATES, fillConsentTemplate } from "@/lib/consentTemplates";
import { Plus, Trash2, FileText, Printer } from "lucide-react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";

function todayStr() { return new Date().toISOString().slice(0, 10); }

const TIPOS = [
  { value: "general", label: "General" },
  { value: "certificado", label: "Certificado médico" },
  { value: "incapacidad", label: "Incapacidad" },
  { value: "remision", label: "Remisión / Interconsulta" },
  { value: "otro", label: "Otro" },
];

function generateDocPdf(item, patient, isConsent = false) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  const margin = 48;

  doc.setFillColor(27, 67, 50);
  doc.rect(0, 0, W, 80, "F");
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(margin, 24, 30, 30, 3, 3, "F");
  doc.setTextColor(27, 67, 50); doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text("DC", margin + 9, 44);
  doc.setTextColor(255); doc.setFontSize(18); doc.text("DentCloud", margin + 44, 42);
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text(isConsent ? "Consentimiento informado" : "Documento clínico", margin + 44, 56);

  let y = 110;
  doc.setTextColor(0); doc.setFont("helvetica", "bold"); doc.setFontSize(14);
  doc.text(isConsent ? `Consentimiento — ${item.procedimiento}` : item.titulo, margin, y); y += 22;

  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(110);
  doc.text(`Paciente: ${patient.name}${patient.document_number ? ` (${patient.document_type || ""} ${patient.document_number})` : ""}`, margin, y); y += 14;
  doc.text(`Fecha: ${item.fecha}`, margin, y); y += 14;
  if (!isConsent && item.tipo) { doc.text(`Tipo: ${item.tipo}`, margin, y); y += 14; }
  y += 8;

  doc.setTextColor(0); doc.setFontSize(10);
  const text = isConsent ? item.contenido : item.contenido;
  const wrap = doc.splitTextToSize(text, W - 2 * margin);
  doc.text(wrap, margin, y); y += wrap.length * 12 + 30;

  if (isConsent) {
    if (y > 600) { doc.addPage(); y = 80; }
    doc.setDrawColor(180);
    doc.line(margin, y, margin + 220, y);
    doc.line(W - margin - 220, y, W - margin, y);
    doc.setFont("helvetica", "bold"); doc.setFontSize(9);
    doc.text("Firma del paciente / representante", margin, y + 14);
    doc.text("Firma del odontólogo", W - margin - 110, y + 14, { align: "center" });
    if (item.firmado_por) { doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.text(item.firmado_por, margin, y + 26); }
    if (item.doctor?.name) { doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.text(item.doctor.name, W - margin - 110, y + 26, { align: "center" }); }
  } else {
    doc.setDrawColor(180);
    doc.line(W - margin - 220, y, W - margin, y);
    doc.setFont("helvetica", "bold"); doc.setFontSize(9);
    doc.text("Firma del odontólogo", W - margin - 110, y + 14, { align: "center" });
    if (item.doctor?.name) { doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.text(item.doctor.name, W - margin - 110, y + 26, { align: "center" }); }
  }

  const fname = `${isConsent ? "consentimiento" : "doc"}_${patient.name.replace(/\s+/g, "_")}_${item.fecha}.pdf`;
  doc.save(fname);
}

export default function ClinicalDocsList({ patientId, patient, kind = "doc" }) {
  // kind: "doc" -> /clinical-docs ; "consent" -> /consents
  const isConsent = kind === "consent";
  const { user } = useAuth();
  const [templateId, setTemplateId] = useState("");
  const endpointGet = isConsent ? `/patients/${patientId}/consents` : `/patients/${patientId}/clinical-docs`;
  const endpointDel = (id) => isConsent ? `/consents/${id}` : `/clinical-docs/${id}`;

  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(
    isConsent
      ? { fecha: todayStr(), procedimiento: "", contenido: "", firmado_por: "" }
      : { fecha: todayStr(), tipo: "general", titulo: "", contenido: "" }
  );

  const load = async () => {
    try { const { data } = await api.get(endpointGet); setItems(data); } catch {}
  };
  useEffect(() => { load(); }, [patientId, kind]); // eslint-disable-line

  const applyTemplate = (id) => {
    setTemplateId(id);
    const t = CONSENT_TEMPLATES.find((x) => x.id === id);
    if (!t) return;
    setForm((f) => ({
      ...f,
      procedimiento: t.procedimiento,
      contenido: fillConsentTemplate(t, { patient, doctorName: user?.name }),
      firmado_por: f.firmado_por || patient?.name || "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(endpointGet, form);
      toast.success(isConsent ? "Consentimiento guardado" : "Documento guardado");
      setForm(isConsent
        ? { fecha: todayStr(), procedimiento: "", contenido: "", firmado_por: "" }
        : { fecha: todayStr(), tipo: "general", titulo: "", contenido: "" });
      setOpen(false); setTemplateId(""); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar?")) return;
    try { await api.delete(endpointDel(id)); load(); } catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <div className="space-y-4" data-testid={`${kind}-tab`}>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-bold text-2xl tracking-tight">
            {isConsent ? "Consentimientos informados" : "Documentos clínicos"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isConsent ? "Genera y archiva consentimientos firmables." : "Certificados, incapacidades, remisiones y otros."}
          </p>
        </div>
        <button onClick={() => setOpen((v) => !v)} className="btn-primary">
          <Plus className="w-4 h-4" /> {open ? "Cancelar" : (isConsent ? "Nuevo consentimiento" : "Nuevo documento")}
        </button>
      </div>

      {open && (
        <form onSubmit={submit} className="card-flat p-5 space-y-3">
          {isConsent && (
            <div className="rounded-sm border border-primary/30 bg-primary/5 p-3">
              <label className="label-eyebrow block mb-1">Plantilla predeterminada</label>
              <select
                className="input-flat"
                data-testid="consent-template-select"
                value={templateId}
                onChange={(e) => applyTemplate(e.target.value)}
              >
                <option value="">— Seleccionar una plantilla ({CONSENT_TEMPLATES.length} disponibles) —</option>
                {CONSENT_TEMPLATES.map((t) => <option key={t.id} value={t.id}>{t.procedimiento}</option>)}
              </select>
              <p className="text-xs text-muted-foreground mt-1.5">
                Al elegir una plantilla se completan automáticamente el procedimiento y el texto con los datos del paciente. Puedes editar el texto antes de guardar.
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="label-eyebrow block mb-1">Fecha</label>
              <input type="date" required className="input-flat font-mono" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
            </div>
            {!isConsent ? (
              <>
                <div>
                  <label className="label-eyebrow block mb-1">Tipo</label>
                  <select className="input-flat" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                    {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-eyebrow block mb-1">Título *</label>
                  <input className="input-flat" required value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} />
                </div>
              </>
            ) : (
              <div className="sm:col-span-2">
                <label className="label-eyebrow block mb-1">Procedimiento *</label>
                <input className="input-flat" required value={form.procedimiento} onChange={(e) => setForm({ ...form, procedimiento: e.target.value })} placeholder="Ej. Extracción tercer molar 18" />
              </div>
            )}
          </div>
          <div>
            <label className="label-eyebrow block mb-1">{isConsent ? "Texto del consentimiento" : "Contenido"} *</label>
            <textarea required rows={isConsent && form.contenido ? 18 : 8} data-testid={`${kind}-content-textarea`} className="input-flat h-auto py-2 font-mono text-xs" value={form.contenido} onChange={(e) => setForm({ ...form, contenido: e.target.value })} />
          </div>
          {isConsent && (
            <div>
              <label className="label-eyebrow block mb-1">Firmado por (nombre del paciente o representante)</label>
              <input className="input-flat" value={form.firmado_por} onChange={(e) => setForm({ ...form, firmado_por: e.target.value })} />
            </div>
          )}
          <button type="submit" className="btn-primary">Guardar</button>
        </form>
      )}

      <div className="space-y-3">
        {items.length === 0 ? (
          <div className="card-flat p-12 text-center text-sm text-muted-foreground">
            {isConsent ? "Sin consentimientos registrados." : "Sin documentos registrados."}
          </div>
        ) : items.map((it) => (
          <div key={it.id} className="card-flat p-4" data-testid={`${kind}-${it.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 text-primary" />
                  <span className="font-mono text-xs text-muted-foreground">{it.fecha}</span>
                  {!isConsent && it.tipo && <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm bg-muted">{it.tipo}</span>}
                </div>
                <div className="font-bold text-sm">{isConsent ? it.procedimiento : it.titulo}</div>
                <div className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap line-clamp-3">{it.contenido}</div>
                {isConsent && it.firmado_por && (
                  <div className="text-xs text-primary mt-1 font-bold">Firmado: {it.firmado_por}</div>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => generateDocPdf(it, patient, isConsent)} className="p-2 text-primary hover:bg-primary/10 rounded-sm" title="Imprimir PDF">
                  <Printer className="w-4 h-4" />
                </button>
                <button onClick={() => remove(it.id)} className="p-2 text-muted-foreground hover:text-destructive">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
